import React, { memo, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { themeColors } from "../../utils/theme";
import { useSESPerformanceTrends } from "../../hooks/useSESPerformanceTrends";
import { useSESReputation } from "../../hooks/useSESReputation";
import { roundToTwo, type EngagementTimeRange } from "../../utils/engagementAnalytics";
import { useEngagementCampaignFilter } from "../../contexts/EngagementCampaignFilterContext";

interface AnalyticsSummaryProps {
  timeRange: EngagementTimeRange;
  refreshToken: number;
}

type MonthlySesRate = {
  month: string;
  deliveryRate: number;
  bounceRate: number;
  complaintRate: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
};

const AnalyticsSummary: React.FC<AnalyticsSummaryProps> = ({ timeRange, refreshToken }) => {
  const theme = useTheme();
  const { normalizedCampaignId } = useEngagementCampaignFilter();
  const {
    dataset: sesTrendData,
    placeholderText: sesTrendPlaceholder,
    isLoading: sesTrendsLoading,
    refetch: refetchTrends,
  } = useSESPerformanceTrends(timeRange, normalizedCampaignId);
  const {
    data: sesReputation,
    isLoading: reputationLoading,
    refetch: refetchReputation,
  } = useSESReputation();

  useEffect(() => {
    if (refreshToken === 0) {
      return;
    }
    refetchTrends();
    refetchReputation();
  }, [refetchReputation, refetchTrends, refreshToken]);

  const tooltipStyles = useMemo(
    () => ({
      content: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "4px",
        padding: "8px",
        color: "#FFFFFF",
      },
      label: { color: "#FFFFFF" },
      item: { color: "#FFFFFF" },
    }),
    [theme]
  );

  const monthlySesSummary: MonthlySesRate[] = useMemo(() => {
    const monthMap = new Map<
      string,
      {
        label: string;
        sent: number;
        delivered: number;
        bounced: number;
        complained: number;
        sortKey: number;
      }
    >();

    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthStart.setHours(0, 0, 0, 0);
      const key = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
      const label = monthStart.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      monthMap.set(key, {
        label,
        sent: 0,
        delivered: 0,
        bounced: 0,
        complained: 0,
        sortKey: monthStart.getTime(),
      });
    }

    sesTrendData.forEach((entry) => {
      const date = new Date(entry.date);
      if (Number.isNaN(date.getTime())) {
        console.warn('Invalid date in sesTrendData:', entry.date);
        return;
      }

      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const monthEntry = monthMap.get(key);
      if (!monthEntry) {
        return;
      }

      monthEntry.sent += entry.sent;
      monthEntry.delivered += entry.delivered;
      monthEntry.bounced += entry.bounced;
      monthEntry.complained += entry.complained;
    });

    const result = Array.from(monthMap.values())
      .sort((a, b) => a.sortKey - b.sortKey)
      .map((entry) => {
        const deliveryRate =
          entry.sent > 0 ? roundToTwo((entry.delivered / entry.sent) * 100) : 0;
        const bounceRate =
          entry.sent > 0 ? roundToTwo((entry.bounced / entry.sent) * 100) : 0;
        const complaintRate =
          entry.delivered > 0
            ? roundToTwo((entry.complained / entry.delivered) * 100)
            : 0;

        return {
          month: entry.label,
          deliveryRate,
          bounceRate,
          complaintRate,
          sent: entry.sent,
          delivered: entry.delivered,
          bounced: entry.bounced,
          complained: entry.complained,
        };
      });

    console.log('Monthly SES Summary computed:', {
      rawDataCount: sesTrendData.length,
      monthlyBuckets: result,
      hasData: result.some(r => r.sent > 0)
    });
    return result;
  }, [sesTrendData]);

  const hasSesTrendData = useMemo(
    () =>
      sesTrendData.some(
        (entry) =>
          entry.sent > 0 ||
          entry.delivered > 0 ||
          entry.opened > 0 ||
          entry.clicked > 0 ||
          entry.bounced > 0 ||
          entry.complained > 0
      ),
    [sesTrendData]
  );
  
  const hasMonthlyData = useMemo(
    () => monthlySesSummary.some(
      (entry) => entry.sent > 0 || entry.delivered > 0 || entry.bounced > 0 || entry.complained > 0
    ),
    [monthlySesSummary]
  );

  const sesChartEmpty = !sesTrendsLoading && !hasMonthlyData;
  const sesChartEmptyMessage =
    sesTrendPlaceholder ?? 'No SES delivery data available for this selection.';

  console.log('SES Chart Debug:', {
    loading: sesTrendsLoading,
    hasSesTrendData,
    hasMonthlyData,
    sesChartEmpty,
    trendDataLength: sesTrendData.length,
    monthlySummaryLength: monthlySesSummary.length,
  });

  const reputationSummary = useMemo(() => {
    if (!sesReputation) {
      return [];
    }

    const formatPercent = (value: number) =>
      `${(Number(value) || 0).toFixed(1)}%`;

    return [
      {
        label: "Delivery Rate",
        value: formatPercent(sesReputation.deliveryRate),
      },
      {
        label: "Bounce Rate",
        value: formatPercent(sesReputation.bounceRate),
      },
      {
        label: "Complaint Rate",
        value: formatPercent(sesReputation.complaintRate),
      },
      {
        label: "Engagement Rate",
        value: formatPercent(sesReputation.engagementRate),
      },
    ];
  }, [sesReputation]);

  return (
    <Grid container spacing={3}>
      {/* Key Metrics */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: `${theme.palette.background.default}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            SES Delivery, Bounce & Complaint Rates (Last 6 Months)
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 1,
            }}
          >
            {reputationLoading && !reputationSummary.length ? (
              <CircularProgress size={24} />
            ) : reputationSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No SES reputation data available.
              </Typography>
            ) : (
              reputationSummary.map((metric) => (
                <Box
                  key={metric.label}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    minWidth: 140,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {metric.label}
                  </Typography>
                  <Typography variant="h6">{metric.value}</Typography>
                </Box>
              ))
            )}
          </Box>
          <Box
            sx={{
              height: 200,
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {sesTrendsLoading ? (
              <CircularProgress size={32} />
            ) : sesChartEmpty ? (
              <Typography variant="body2" color="text.secondary">
                {sesChartEmptyMessage}
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={monthlySesSummary}
                  margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: theme.palette.text.secondary }}
                  />
                  <YAxis
                    tick={{ fill: theme.palette.text.secondary }}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={tooltipStyles.content}
                    labelStyle={{ ...tooltipStyles.label, fontWeight: "bold" }}
                    itemStyle={tooltipStyles.item}
                    formatter={(value: number, name: string) => [
                      `${(Number(value) || 0).toFixed(2)}%`,
                      name === "deliveryRate"
                        ? "Delivery Rate"
                        : name === "bounceRate"
                          ? "Bounce Rate"
                          : "Complaint Rate",
                    ]}
                    labelFormatter={(
                      label: string,
                      payload: Array<{ payload?: MonthlySesRate }>
                    ) => {
                      const firstPayload = payload?.[0]?.payload;
                      if (!firstPayload) {
                        return label;
                      }
                      return `${label} • Sent ${firstPayload.sent.toLocaleString()}, Delivered ${firstPayload.delivered.toLocaleString()}`;
                    }}
                  />
                  <Legend
                    wrapperStyle={{ color: theme.palette.text.secondary }}
                  />
                  <Line
                    type="monotone"
                    dataKey="deliveryRate"
                    stroke={themeColors.primary}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="bounceRate"
                    stroke={theme.palette.warning.main}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="complaintRate"
                    stroke={theme.palette.error.main}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default memo(AnalyticsSummary);
